import { AllValues, FilterValues } from "../sortAndFilter/contexts/SortAndFilterContext";
import { InventoryConfiguration } from "../shared/domain/InventoryConfiguration";
import { NAME_TYPE } from "../shared/constants";
import { InventoryData } from "../shared/domain/InventoryData";
import { PromotionData } from "../shared/domain/PromotionData";
import { ExtraData } from "../shared/domain/ExtraData";
import { BundleInventoryData } from "../shared/domain/BundleInventoryData";

const sortByKey = (
  inventory: InventoryData[] | BundleInventoryData[],
  key: keyof InventoryConfiguration
): InventoryData[] | BundleInventoryData[] => {
  return inventory.sort((a, b): number => {
    if (!a.configuration[key] || !b.configuration[key]) {
      return 0;
    }
    if (a.configuration[key] < b.configuration[key]) {
      return -1;
    }
    if (a.configuration[key] > b.configuration[key]) {
      return 1;
    }
    return 0;
  });
};

const sortByIndex = (array: InventoryData[] | BundleInventoryData[], index: number) => {
  return array.sort((a, b) => {
    const nameA = a.name.split(", ")[index];
    const nameB = b.name.split(", ")[index];

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
};

const getSortedInventory = (
  inventories: InventoryData[] | BundleInventoryData[],
  nameType: NAME_TYPE,
  orderValue: string,
  inventoryLineSearched?: string
): InventoryData[] | BundleInventoryData[] => {
  if (inventoryLineSearched || !orderValue) {
    return inventories;
  }
  let key: keyof InventoryConfiguration =
    orderValue === "occupancy" ? "occupancy" : "accommodation";
  if (orderValue === "rate") key = "condition";
  if (orderValue === "mealPlans") key = "mealPlan";

  if (nameType === NAME_TYPE.SHORT) {
    return sortByKey(inventories, key);
  }

  if (nameType === NAME_TYPE.FULL) {
    switch (orderValue) {
      case "condition":
        return inventories.sort((a, b) => {
          const nameA = a.name.split(", ")[3].split(" (")[0];
          const nameB = b.name.split(", ")[3].split(" (")[0];

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });

      case "mealPlan":
        return sortByIndex(inventories, 2);

      case "occupancy":
        return sortByIndex(inventories, 1);

      case "accommodation":
      default:
        return sortByIndex(inventories, 0);
    }
  }
  return inventories;
};

const filterByInventoryLineSearched = (inventories: any, inventoryLineSearched?: string) =>
  inventoryLineSearched
    ? inventories.filter((it: any) => it.ticker.includes(inventoryLineSearched))
    : inventories;

const filterByBundleInventoryLineSearched = (inventories: any, inventoryLineSearched?: string) =>
  inventoryLineSearched
    ? inventories.filter((it: any) =>
        it.ticker.replace(`${it.ticker}_`, "").includes(inventoryLineSearched)
      )
    : inventories;

const filterBundleByAccommodation = (
  inventories: BundleInventoryData[],
  accommodation?: FilterValues[]
) =>
  accommodation && accommodation.length
    ? inventories.filter(it =>
        accommodation.some(acc => acc.value === it.configuration.accommodation)
      )
    : inventories;

const filterBundleByCondition = (inventories: BundleInventoryData[], condition?: FilterValues[]) =>
  condition && condition.length
    ? inventories.filter(it => condition.some(con => con.value === it.configuration.condition))
    : inventories;

const filterBundleByMealPlan = (inventories: BundleInventoryData[], mealPlan?: FilterValues[]) =>
  mealPlan && mealPlan.length
    ? inventories.filter(it => mealPlan.some(mea => mea.value === it.configuration.mealPlan))
    : inventories;

const filterBundleByOccupancy = (inventories: BundleInventoryData[], occupancy?: FilterValues[]) =>
  occupancy && occupancy.length
    ? inventories.filter(it => occupancy.some(occ => occ.value === it.configuration.occupancy))
    : inventories;

const filterByFields = <T>(inventories: any, fields?: FilterValues[]) => {
  if (!fields || !fields.length) {
    return inventories;
  }
  return inventories.reduce((prevInvData: any, it: any) => {
    const result = { ...it };
    result.dailyValues = Object.entries(result.dailyValues)
      .filter(([valueType, _values]) => fields.some((field: any) => field.value === valueType))
      .reduce((prevDaiVal, [valueType, values]: any) => {
        return { ...prevDaiVal, [valueType]: { ...values } };
      }, {});
    return [...prevInvData, result];
  }, [] as T[]);
};

const filterByAccommodation = (inventories: InventoryData[], accommodation?: FilterValues[]) =>
  accommodation && accommodation.length
    ? inventories.filter(it =>
        accommodation.some(acc => acc.value === it.configuration.accommodation)
      )
    : inventories;

const filterByCondition = (inventories: InventoryData[], condition?: FilterValues[]) =>
  condition && condition.length
    ? inventories.filter(it => condition.some(con => con.value === it.configuration.condition))
    : inventories;

const filterByMealPlan = (inventories: InventoryData[], mealPlan?: FilterValues[]) =>
  mealPlan && mealPlan.length
    ? inventories.filter(it => mealPlan.some(mea => mea.value === it.configuration.mealPlan))
    : inventories;

const filterByOccupancy = (inventories: InventoryData[], occupancy?: FilterValues[]) =>
  occupancy && occupancy.length
    ? inventories.filter(it => occupancy.some(occ => occ.value === it.configuration.occupancy))
    : inventories;

const filterByMasterLines = <T>(
  inventories: any,
  showOnlyMasterLines?: boolean,
  inventoryLineSearched?: string
) => {
  if (!showOnlyMasterLines || inventoryLineSearched) {
    return inventories;
  }
  return inventories.reduce((prevInvData: T[], it: any) => {
    const result = { ...it };
    result.dailyValues = Object.entries(result.dailyValues)
      .filter(([_valueType, values]: any) => !values?.parentTicker)
      .reduce((prevDaiVal, [valueType, values]: any) => {
        return { ...prevDaiVal, [valueType]: { ...values } };
      }, {});
    return [...prevInvData, result];
  }, [] as T[]);
};

export const getFilteredInventories = (
  inventories: InventoryData[],
  nameType: NAME_TYPE,
  filters: AllValues
): InventoryData[] => {
  const {
    fields,
    roomTypes,
    rates,
    mealPlans,
    occupancies,
    showOnlyMasterLines,
    order,
    inventoryLineSearched,
  } = filters;

  if (!inventories) {
    return [];
  }

  let filteredInv = [...inventories];
  filteredInv = filterByInventoryLineSearched(filteredInv, inventoryLineSearched);
  filteredInv = filterByFields<InventoryData>(filteredInv, fields);
  filteredInv = filterByAccommodation(filteredInv, roomTypes);
  filteredInv = filterByCondition(filteredInv, rates);
  filteredInv = filterByMealPlan(filteredInv, mealPlans);
  filteredInv = filterByOccupancy(filteredInv, occupancies);
  filteredInv = filterByMasterLines<InventoryData>(
    filteredInv,
    showOnlyMasterLines,
    inventoryLineSearched
  );
  if (order) {
    filteredInv = getSortedInventory(
      filteredInv,
      nameType,
      order.value,
      inventoryLineSearched
    ) as InventoryData[];
  }

  return filteredInv;
};

export const getFilteredBundleInventories = (
  bundleInventories: BundleInventoryData[],
  nameType: NAME_TYPE,
  filters: AllValues
): BundleInventoryData[] => {
  const {
    fields,
    roomTypes,
    rates,
    mealPlans,
    occupancies,
    showOnlyMasterLines,
    order,
    inventoryLineSearched,
  } = filters;

  if (!bundleInventories) {
    return [];
  }

  let filteredInv = [...bundleInventories];
  filteredInv = filterByBundleInventoryLineSearched(filteredInv, inventoryLineSearched);
  filteredInv = filterByFields<BundleInventoryData>(filteredInv, fields);
  filteredInv = filterBundleByAccommodation(filteredInv, roomTypes);
  filteredInv = filterBundleByCondition(filteredInv, rates);
  filteredInv = filterBundleByMealPlan(filteredInv, mealPlans);
  filteredInv = filterBundleByOccupancy(filteredInv, occupancies);
  filteredInv = filterByMasterLines<BundleInventoryData>(
    filteredInv,
    showOnlyMasterLines,
    inventoryLineSearched
  );
  if (order) {
    filteredInv = getSortedInventory(filteredInv, nameType, order.value, inventoryLineSearched);
  }

  return filteredInv;
};

export const getFilteredPromotions = (promotions: PromotionData[], filters: AllValues) => {
  const {
    fields,
    roomTypes,
    rates,
    mealPlans,
    occupancies,
    inventoryLineSearched,
    showOnlyMasterLines,
  } = filters;
  if (roomTypes || rates || mealPlans || occupancies) {
    return [];
  }
  let filteredPromos = [...promotions];
  filteredPromos = filterByInventoryLineSearched(filteredPromos, inventoryLineSearched);
  filteredPromos = filterByFields<PromotionData>(filteredPromos, fields);
  filteredPromos = filterByMasterLines<PromotionData>(
    filteredPromos,
    showOnlyMasterLines,
    inventoryLineSearched
  );
  return filteredPromos;
};

export const getFilteredExtras = (extras: ExtraData[], filters: AllValues) => {
  const {
    fields,
    roomTypes,
    rates,
    mealPlans,
    occupancies,
    inventoryLineSearched,
    showOnlyMasterLines,
  } = filters;
  if (roomTypes || rates || mealPlans || occupancies) {
    return [];
  }
  let filteredExtras = [...extras];
  filteredExtras = filterByInventoryLineSearched(filteredExtras, inventoryLineSearched);
  filteredExtras = filterByFields<ExtraData>(filteredExtras, fields);
  filteredExtras = filterByMasterLines<ExtraData>(
    filteredExtras,
    showOnlyMasterLines,
    inventoryLineSearched
  );
  return filteredExtras;
};
